import { Button, Card, CardContent, Loader } from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { LogOutIcon } from "lucide-react";
import {
  describeViewerError,
  publicFormsViewerApi,
  viewerFormResponsesApi,
} from "@/modules/forms/presentation/public/public-forms-viewer-api";
import { FormResponsesPanel } from "@/modules/forms/presentation/ui/responses/FormResponsesPanel";
import { config } from "@/shared/lib/config";

export function PublicFormViewerResponsesPage() {
  const { formId } = useParams({ from: "/f/$formId/view/responses" });
  const resolvedId =
    typeof formId === "string" && formId.length > 0 ? formId : "";
  const navigate = useNavigate({ from: "/f/$formId/view/responses" });
  const search = useSearch({ from: "/f/$formId/view/responses" });

  const context = useQuery({
    queryKey: ["public-form-viewer-context", resolvedId],
    queryFn: () => publicFormsViewerApi.getContext(resolvedId),
    enabled: resolvedId.length > 0,
    retry: false,
  });

  if (!resolvedId) {
    return <ErrorPane message="ລິ້ງບໍ່ຖືກຕ້ອງ" />;
  }

  if (context.isLoading) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-muted-foreground">
        <Loader />
        <span className="text-sm">ກຳລັງໂຫຼດຄຳຕອບ…</span>
      </div>
    );
  }

  if (context.isError) {
    const code = (context.error as Error & { code?: string }).code ?? "UNKNOWN";
    if (code === "VIEWER_SESSION_REQUIRED") {
      void navigate({
        to: "/f/$formId/view",
        params: { formId: resolvedId },
      });
      return null;
    }
    return <ErrorPane message={describeViewerError(code)} />;
  }

  const ctx = context.data;
  if (!ctx) return null;

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-8 pb-16">
      <p className="mb-6 text-muted-foreground text-xs">{config.brandName}</p>
      <FormResponsesPanel
        formId={resolvedId}
        formTitle={ctx.form.title}
        fields={ctx.fields}
        responsesApi={viewerFormResponsesApi}
        search={{
          limit: search.limit,
          offset: search.offset,
          filt: search.filt,
        }}
        onNavigateSearch={(next) =>
          navigate({
            search: {
              limit: next.limit ?? search.limit,
              offset: next.offset,
              filt: next.filt,
            },
          })
        }
        headerActions={
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={async () => {
              await publicFormsViewerApi.logout(resolvedId);
              await navigate({
                to: "/f/$formId/view",
                params: { formId: resolvedId },
              });
            }}
          >
            <LogOutIcon className="mr-2 h-4 w-4" />
            ອອກຈາກການເບິ່ງ
          </Button>
        }
      />
    </main>
  );
}

function ErrorPane({ message }: { message: string }) {
  return (
    <div className="flex min-h-[50vh] items-center justify-center p-6">
      <Card className="w-full max-w-lg border-destructive/40">
        <CardContent className="pt-6">
          <p className="font-semibold text-lg">{message}</p>
        </CardContent>
      </Card>
    </div>
  );
}
